import { AnimatePresence, motion } from 'framer-motion';
import { differenceInSeconds, parseISO } from 'date-fns';
import { AlertTriangle, ChevronLeft, ChevronRight, ExternalLink, ShieldAlert } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PendingRevisionProduct } from '../hooks/usePendingRevisions';

interface RevisionAlertBannerProps {
  products: PendingRevisionProduct[];
}

function Countdown({ deadlineAt, onExpire }: { deadlineAt: string; onExpire: () => void }) {
  const { t } = useTranslation('dashboard');
  const [remaining, setRemaining] = useState<number>(() =>
    Math.max(0, differenceInSeconds(parseISO(deadlineAt), new Date()))
  );

  useEffect(() => {
    setRemaining(Math.max(0, differenceInSeconds(parseISO(deadlineAt), new Date())));
  }, [deadlineAt]);

  useEffect(() => {
    if (remaining <= 0) {
      onExpire();
      return;
    }
    const interval = setInterval(() => {
      setRemaining((prev) => {
        const next = Math.max(0, prev - 1);
        if (next === 0) onExpire();
        return next;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [remaining, onExpire]);

  const hours = Math.floor(remaining / 3600);
  const minutes = Math.floor((remaining % 3600) / 60);
  const seconds = remaining % 60;

  const isUrgent = remaining < 3600 * 6; // menos de 6h

  if (remaining <= 0) {
    return (
      <span className='text-amber-400 font-semibold text-xs animate-pulse'>
        {t('revision.deadline_expired')}
      </span>
    );
  }

  return (
    <span className={`font-mono text-xs font-semibold tabular-nums ${isUrgent ? 'text-amber-400' : 'text-yellow-300'}`}>
      {String(hours).padStart(2, '0')}:{String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
    </span>
  );
}

export default function RevisionAlertBanner({ products }: RevisionAlertBannerProps) {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [expiredFlags, setExpiredFlags] = useState<Record<number, boolean>>({});

  if (!products.length) return null;

  const total = products.length;
  const product = products[current];

  const isExpired = differenceInSeconds(parseISO(product.deadline_at), new Date()) <= 0 || !!expiredFlags[product.id];

  const handleExpire = useCallback(() => {
    setExpiredFlags((prev) => {
      if (prev[product.id]) return prev;
      return { ...prev, [product.id]: true };
    });
  }, [product.id]);

  const goNext = () => setCurrent((prev) => (prev + 1) % total);
  const goPrev = () => setCurrent((prev) => (prev - 1 + total) % total);

  return (
    <AnimatePresence mode='wait'>
      <motion.div
        key={current}
        initial={{ opacity: 0, y: -4 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 4 }}
        transition={{ duration: 0.2 }}
        className={`w-full border rounded-xl overflow-hidden bg-zinc-950/95 md:bg-zinc-950/70 md:backdrop-blur-sm ${isExpired
          ? 'border-zinc-800'
          : 'border-zinc-800'
          }`}
      >
        <div className={`flex items-center justify-between px-4 py-2.5 border-b ${isExpired ? 'border-zinc-800/80' : 'border-zinc-800/80'
          }`}>
          <div className='flex items-center gap-2'>
            <span className='relative flex h-2 w-2'>
              {!isExpired && (
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60 bg-yellow-400 max-md:hidden" />
              )}
              <span className={`relative inline-flex rounded-full h-2 w-2 ${isExpired ? 'bg-amber-500' : 'bg-yellow-400'
                }`} />
            </span>
            <span className={`text-xs font-semibold uppercase tracking-wide ${isExpired ? 'text-amber-400' : 'text-zinc-400'
              }`}>
              {total === 1
                ? t('revision.pending_singular')
                : t('revision.pending_plural', { count: total })}
            </span>
          </div>

          {total > 1 && (
            <div className='flex items-center gap-1'>
              <button
                onClick={goPrev}
                className='flex items-center justify-center w-6 h-6 rounded-md text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors'
              >
                <ChevronLeft className='w-3.5 h-3.5' />
              </button>
              <span className='text-xs text-zinc-500 tabular-nums min-w-[3ch] text-center'>
                {current + 1}/{total}
              </span>
              <button
                onClick={goNext}
                className='flex items-center justify-center w-6 h-6 rounded-md text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors'
              >
                <ChevronRight className='w-3.5 h-3.5' />
              </button>
            </div>
          )}
        </div>

        <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 px-4 sm:px-5 py-3 sm:py-4'>
          <div className='flex items-start gap-3 sm:gap-3.5 min-w-0 flex-1'>
            <span className={`hidden sm:flex items-center justify-center w-10 h-10 rounded-full border shrink-0 mt-0.5 ${isExpired
              ? 'bg-amber-500/10 border-amber-500/20'
              : 'bg-yellow-500/10 border-yellow-500/20'
              }`}>
              {isExpired
                ? <ShieldAlert className='w-5 h-5 text-amber-500' />
                : <AlertTriangle className='w-5 h-5 text-yellow-400' />
              }
            </span>
            <div className='min-w-0 w-full'>
              <p className='text-white text-sm leading-snug'>
                O produto <span className="font-semibold">{product.name}</span> precisa de atenção
              </p>

              {product.review_message && (
                <div className="mt-2 sm:mt-2.5 bg-zinc-900/50 border border-zinc-800/80 rounded-lg p-2.5 sm:p-3 sm:max-w-2xl relative overflow-hidden">
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-yellow-500/30 rounded-l-lg"></div>
                  <p className="text-[11px] sm:text-xs text-zinc-300 italic leading-relaxed pl-1.5 break-words">
                    "{product.review_message}"
                  </p>
                </div>
              )}

              <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-1 sm:gap-x-2 sm:gap-y-1 mt-2 sm:mt-2.5">
                <p className='text-zinc-400 text-xs font-medium'>
                  {t('revision.time_remaining')}: <Countdown deadlineAt={product.deadline_at} onExpire={handleExpire} />
                </p>
                <span className="text-zinc-700 hidden sm:inline">•</span>
                <p className={`text-[11px] sm:text-xs ${isExpired ? 'text-amber-500' : 'text-zinc-500'}`}>
                  {isExpired
                    ? t('revision.auto_block_imminent')
                    : t('revision.auto_block_warning')}
                </p>
              </div>
            </div>
          </div>

          <button
            onClick={() => navigate(`/products/edit-product/${product.id}/geral`)}
            className={`flex items-center justify-center gap-2 shrink-0 px-4 py-2.5 sm:px-5 sm:py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 w-full sm:w-auto shadow-sm ${isExpired
              ? 'bg-primary hover:bg-primary/90 text-zinc-950'
              : 'bg-white hover:bg-zinc-100 text-zinc-950'
              }`}
          >
            {isExpired ? t('revision.fix_urgent') : t('revision.fix_now')}
            <ExternalLink className='h-4 w-4' />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
